import React, { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { NavLink, useLocation } from "react-router-dom"
import { toggleSidebar } from "../../redux/store/themeConfigSlice"
import { IRootState } from "../../redux/store"
import {
  BarChart2,
  BookOpenCheck,
  Briefcase,
  CheckCircle,
  Clock,
  CreditCard,
  Hash,
  List,
  MessageSquare,
  PackageSearch,
  Search,
  Settings,
  Share2,
  ShieldCheck,
  Star,
  Tag,
  Users,
  UserX,
  PenTool,
  FileWarning,
  UserPlus,
  Bot,
  Server,
  MessageCircle,
  HeartHandshake,
  UserCheck,
  LayoutGrid,
} from "lucide-react"

const Sidebar = () => {
  const themeConfig = useSelector((state: IRootState) => state.themeConfig)
  const semidark = useSelector((state: IRootState) => state.themeConfig.semidark)
  const location = useLocation()
  const dispatch = useDispatch()

  useEffect(() => {
    const selector = document.querySelector(
      ".sidebar ul a[href='" + window.location.pathname + "']",
    )
    if (selector) {
      selector.classList.add("active")
      const ul: any = selector.closest("ul.sub-menu")
      if (ul) {
        let ele: any = ul.closest("li.menu").querySelectorAll(".nav-link") || []
        if (ele.length) {
          ele = ele[0]
          setTimeout(() => {
            ele.click()
          })
        }
      }
    }
  }, [])

  useEffect(() => {
    if (window.innerWidth < 1024 && themeConfig.sidebar) {
      dispatch(toggleSidebar())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  return (
    <div className={semidark ? "dark" : ""}>
      <nav
        className={`sidebar fixed top-0 bottom-0 w-[260px] shadow-[5px_0_25px_0_rgba(94,92,154,0.1)] z-50 transition-all duration-300 ${
          semidark ? "text-white-dark" : ""
        }`}
      >
        <div className="bg-white dark:bg-black h-full overflow-y-auto overscroll-contain scrollbar-thin scrollbar-thumb-gray-900 scrollbar-track-gray-700">
          <div className="flex justify-between items-center px-4 py-3">
            <NavLink to="/" className="main-logo flex items-center shrink-0">
              <img
                className="w-36 ml-[22px] flex-none"
                src="/assets/images/teamfluencer.webp"
                alt="logo"
              />
            </NavLink>

            <button
              type="button"
              className="collapse-icon w-8 h-8 rounded-full flex items-center hover:bg-gray-500/10 dark:hover:bg-dark-light/10 dark:text-white-light transition duration-300 rtl:rotate-180"
              onClick={() => dispatch(toggleSidebar())}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 m-auto"
              >
                <path
                  d="M13 19L7 12L13 5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  opacity="0.5"
                  d="M16.9998 19L10.9998 12L16.9998 5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          <ul className="relative font-semibold space-y-0.5 p-4 py-0">
            <h2 className="py-3 px-7 flex items-center uppercase font-extrabold bg-white-light/30 dark:bg-dark dark:bg-opacity-[0.08] -mx-4 mb-1">
              <svg
                className="w-4 h-5 flex-none hidden"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              <span>ADMIN</span>
            </h2>

            <li className="nav-item">
              <ul className="flex-col">
                <h2 className="flex justify-center font-extrabold mt-3 mb-2 font-xl">
                  Kontrol Paneli
                </h2>
                <li className="nav-item">
                  <NavLink to="/dashboard/statistics" className="group">
                    <div className="flex items-center">
                      <BarChart2 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        İstatistikler
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/dashboard/hashtag-search" className="group">
                    <div className="flex items-center">
                      <Hash className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Hashtag Araması
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/dashboard/popular-brands" className="group">
                    <div className="flex items-center">
                      <Star className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Popüler Markalar
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/dashboard/wp-messages" className="group">
                    <div className="flex items-center">
                      <MessageSquare className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        WP Mesajları
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/dashboard/user-payments" className="group">
                    <div className="flex items-center">
                      <CreditCard className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Kullanıcı Ödemeleri
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/dashboard/sml-manager" className="group">
                    <div className="flex items-center">
                      <Settings className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        SML Manager
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/dashboard/runpod" className="group">
                    <div className="flex items-center">
                      <Server className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Runpod Workers
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/dashboard/hashtag-pipeline" className="group">
                    <div className="flex items-center">
                      <BarChart2 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Hashtag Pipeline
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/dashboard/external-user-pipeline" className="group">
                    <div className="flex items-center">
                      <UserCheck className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        External User Pipeline
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/dashboard/whatsapp-button" className="group">
                    <div className="flex items-center">
                      <MessageCircle className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        WhatsApp Butonu
                      </span>
                    </div>
                  </NavLink>
                </li>
                <div className="border-t border-gray-300 my-4"></div>
                <h2 className="flex justify-center font-extrabold mt-3 mb-2 font-xl">
                  Kullanıcı İşlemleri
                </h2>
                <li className="nav-item">
                  <NavLink to="/user/find" className="group">
                    <div className="flex items-center">
                      <Search className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Kullanıcı Bul
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/user/create" className="group">
                    <div className="flex items-center">
                      <UserPlus className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Kullanıcı Oluştur
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/user/user-tagged-posts" className="group">
                    <div className="flex items-center">
                      <Tag className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Etiketlenen Gönderileri Bul
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/user/get-all" className="group">
                    <div className="flex items-center">
                      <Users className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Kullanıcıları Listele
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/user/banned-users" className="group">
                    <div className="flex items-center">
                      <UserX className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Banlanmış Kullanıcılar
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/user/do-approval" className="group">
                    <div className="flex items-center">
                      <ShieldCheck className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Profilleri Onayla
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/user/find-waiting-approval" className="group">
                    <div className="flex items-center">
                      <Clock className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Onay Bekleyen Profiller
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/user/bot-account-scanning" className="group">
                    <div className="flex items-center">
                      <Bot className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Bot Hesap Tarama
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/user/users-by-categories" className="group">
                    <div className="flex items-center">
                      <LayoutGrid className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Kategoriye Göre Kullanıcılar
                      </span>
                    </div>
                  </NavLink>
                </li>
                <div className="border-t border-gray-300 my-4"></div>
                <h2 className="flex justify-center font-extrabold mt-3 mb-2 font-xl">
                  Marka İşlemleri
                </h2>
                <li className="nav-item">
                  <NavLink to="/brands/find" className="group">
                    <div className="flex items-center">
                      <Search className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Marka Bul
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/brands/get-all" className="group">
                    <div className="flex items-center">
                      <List className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Tüm Markalar
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/brands/unregistered" className="group">
                    <div className="flex items-center">
                      <FileWarning className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Kaydı Yarıda Kalan Markalar
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/brands/state-stats" className="group">
                    <div className="flex items-center">
                      <BarChart2 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Marka Durum İstatistikleri
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/brands/new-registrations" className="group">
                    <div className="flex items-center">
                      <UserPlus className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Yeni Kayıtlar
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/brands/customer-success" className="group">
                    <div className="flex items-center">
                      <HeartHandshake className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Customer Success Panel
                      </span>
                    </div>
                  </NavLink>
                </li>
                <div className="border-t border-gray-300 my-4"></div>
                <h2 className="flex justify-center font-extrabold mt-3 mb-2 font-xl">
                  Kampanya İşlemleri
                </h2>
                <li className="nav-item">
                  <NavLink to="/campaigns/get-all" className="group">
                    <div className="flex items-center">
                      <PackageSearch className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Tüm Kampanyalar
                      </span>
                    </div>
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink to="/campaigns/statistics" className="group">
                    <div className="flex items-center">
                      <BarChart2 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Kampanya İstatistikleri
                      </span>
                    </div>
                  </NavLink>
                </li>
                <div className="border-t border-gray-300 my-4"></div>
                <h2 className="flex justify-center font-extrabold mt-3 mb-2 font-xl">
                  Başvuru İşlemleri
                </h2>
                <li className="menu nav-item">
                  <NavLink to="/applications/all-applications" className="group">
                    <div className="flex items-center">
                      <List className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Tüm Başvurular
                      </span>
                    </div>
                  </NavLink>
                </li>

                <li className="menu nav-item">
                  <NavLink to="/applications/agency-applications" className="group">
                    <div className="flex items-center">
                      <Briefcase className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Ajans Başvuruları
                      </span>
                    </div>
                  </NavLink>
                </li>

                <li className="menu nav-item">
                  <NavLink to="/applications/content-shared" className="group">
                    <div className="flex items-center">
                      <Share2 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Paylaşılan İçerikler
                      </span>
                    </div>
                  </NavLink>
                </li>

                <li className="menu nav-item">
                  <NavLink to="/applications/application-done" className="group">
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Tamamlanan İş Birlikleri
                      </span>
                    </div>
                  </NavLink>
                </li>

                <li className="menu nav-item">
                  <NavLink to="/applications/application-stats" className="group">
                    <div className="flex items-center">
                      <BarChart2 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Başvuru İstatistikleri
                      </span>
                    </div>
                  </NavLink>
                </li>

                <li className="menu nav-item">
                  <NavLink to="/applications/live-application-stats" className="group">
                    <div className="flex items-center">
                      <BarChart2 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Canlı Başvuru İstatistikleri
                      </span>
                    </div>
                  </NavLink>
                </li>
                <div className="border-t border-gray-300 my-4"></div>
                <h2 className="flex justify-center font-extrabold mt-3 mb-2 font-xl">
                  Blog İşlemleri
                </h2>
                <li className="menu nav-item">
                  <NavLink to="/blog/automated-blog" className="group">
                    <div className="flex items-center">
                      <PenTool className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        Otomatik Blog Oluşturucu
                      </span>
                    </div>
                  </NavLink>
                </li>
                <div className="border-t border-gray-300 my-4"></div>
                <h2 className="flex justify-center font-extrabold mt-3 mb-2 font-xl">API Docs</h2>
                <li className="menu nav-item">
                  <NavLink to="/api-docs/document" className="group">
                    <div className="flex items-center">
                      <BookOpenCheck className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                        API Dökümantasyonu
                      </span>
                    </div>
                  </NavLink>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </nav>
    </div>
  )
}

export default Sidebar
